import React from 'react';

// Info Icon
const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

/**
 * DynamicFormField - Renders form fields based on schema definition
 * 
 * @param {Object} props
 * @param {Object} props.schema - Field schema from API
 * @param {string} props.schema.name - Field name
 * @param {string} props.schema.type - Field type (INTEGER, DOUBLE, STRING, BOOLEAN, SELECT)
 * @param {boolean} props.schema.required - Whether field is required
 * @param {*} props.schema.defaultValue - Default value
 * @param {number} props.schema.minValue - Minimum value (for numbers)
 * @param {number} props.schema.maxValue - Maximum value (for numbers)
 * @param {string} props.schema.description - Field description
 * @param {string} props.schema.unit - Unit label (e.g., 'bars', 'percent')
 * @param {Array} props.schema.options - Options for SELECT type
 * @param {*} props.value - Current field value
 * @param {Function} props.onChange - Change handler (value) => void
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Whether field is disabled
 * @param {string} props.size - Field size ('small' | 'medium')
 * @param {boolean} props.fullWidth - Whether field takes full width
 */
const DynamicFormField = ({
  schema,
  value,
  onChange,
  error,
  disabled = false,
  size = 'small',
  fullWidth = true,
}) => {
  if (!schema) return null;

  const {
    name,
    type,
    required = false,
    defaultValue,
    minValue,
    maxValue,
    description,
    unit,
    options = [],
    allowedValues = [],
  } = schema;

  // Generate label from name (camelCase to Title Case)
  const label = schema.label || name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();

  // Get current value or default
  const currentValue = value !== undefined ? value : defaultValue;

  // Handle value change
  const handleChange = (e) => {
    let newValue = e.target.value;

    // Type conversion
    if (type === 'INTEGER') {
      newValue = newValue === '' ? '' : parseInt(newValue, 10);
    } else if (type === 'DOUBLE' || type === 'number') {
      newValue = newValue === '' ? '' : parseFloat(newValue);
    } else if (type === 'BOOLEAN') {
      newValue = e.target.checked;
    }

    onChange(newValue);
  };

  // Get tooltip text
  const getTooltipText = () => {
    let tooltipText = description || '';
    if (minValue !== undefined || maxValue !== undefined) {
      const rangeText = `Range: ${minValue ?? 'any'} - ${maxValue ?? 'any'}`;
      tooltipText = tooltipText ? `${tooltipText} (${rangeText})` : rangeText;
    }
    return tooltipText;
  };

  const tooltipText = getTooltipText();

  // Render based on type
  switch (type?.toUpperCase()) {
    case 'INTEGER':
    case 'DOUBLE':
    case 'NUMBER':
      return (
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm truncate" title={label}>
              {label}{required && <span className="text-error">*</span>}
            </span>
            {tooltipText && (
              <span className="text-xs text-base-content/60 tooltip" data-tip={tooltipText}>
                <InfoIcon />
              </span>
            )}
          </label>
          <input
            type="number"
            className={`input input-bordered w-full ${error ? 'input-error' : ''}`}
            value={currentValue ?? ''}
            onChange={handleChange}
            required={required}
            disabled={disabled}
            min={minValue}
            max={maxValue}
            step={type?.toUpperCase() === 'INTEGER' ? 1 : 0.01}
          />
          {(error || unit) && (
            <label className="label">
              <span className={`text-xs text-base-content/60 ${error ? 'text-error' : ''}`}>
                {error || (unit ? `Unit: ${unit}` : '')}
              </span>
            </label>
          )}
        </div>
      );

    case 'BOOLEAN':
      return (
        <div className="fieldset">
          <label className="label cursor-pointer justify-start gap-3">
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={!!currentValue}
              onChange={handleChange}
              disabled={disabled}
            />
            <span className="text-sm">{label}</span>
          </label>
        </div>
      );

    case 'SELECT':
      return (
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm">
              {label}{required && <span className="text-error">*</span>}
            </span>
          </label>
          <select
            className={`select select-bordered w-full ${error ? 'select-error' : ''}`}
            value={currentValue ?? ''}
            onChange={handleChange}
            disabled={disabled}
          >
            <option value="" disabled>Select...</option>
            {options.map((option) => (
              <option
                key={option.value ?? option}
                value={option.value ?? option}
              >
                {option.label ?? option}
              </option>
            ))}
          </select>
          {(error || description) && (
            <label className="label">
              <span className={`text-xs text-base-content/60 ${error ? 'text-error' : ''}`}>
                {error || description}
              </span>
            </label>
          )}
        </div>
      );

    case 'STRING':
    case 'TEXT':
    default:
      // If allowedValues provided, render as dropdown
      if (allowedValues && allowedValues.length > 0) {
        return (
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm truncate" title={label}>
                {label}{required && <span className="text-error">*</span>}
              </span>
              {tooltipText && (
                <span className="text-xs text-base-content/60 tooltip" data-tip={tooltipText}>
                  <InfoIcon />
                </span>
              )}
            </label>
            <select
              className={`select select-bordered w-full ${error ? 'select-error' : ''}`}
              value={currentValue ?? ''}
              onChange={handleChange}
              disabled={disabled}
            >
              <option value="">{required ? 'Select...' : 'None (Optional)'}</option>
              {allowedValues.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
            {error && (
              <label className="label">
                <span className="text-xs text-error">
                  {error}
                </span>
              </label>
            )}
          </div>
        );
      }

      // Default: render as text input
      return (
        <div className="fieldset w-full">
          <label className="label">
            <span className="text-sm truncate" title={label}>
              {label}{required && <span className="text-error">*</span>}
            </span>
            {tooltipText && (
              <span className="text-xs text-base-content/60 tooltip" data-tip={tooltipText}>
                <InfoIcon />
              </span>
            )}
          </label>
          <input
            type="text"
            className={`input input-bordered w-full ${error ? 'input-error' : ''}`}
            value={currentValue ?? ''}
            onChange={handleChange}
            required={required}
            disabled={disabled}
          />
          {(error || description) && (
            <label className="label">
              <span className={`text-xs text-base-content/60 ${error ? 'text-error' : ''}`}>
                {error || description}
              </span>
            </label>
          )}
        </div>
      );
  }
};

export default DynamicFormField;
